/**
 * The caller's effective permissions for the current org, and a convenience
 * `useHasAction`. Ported from `bimgo-ai-web`'s `usePermissions`, adapted to
 * 2.5's plain fetch + effect (no react-query).
 *
 * Keyed on `orgId` (the token is read from a ref at fetch time), so the frequent
 * token rotations from the single-session guard do not refetch — the same fix
 * `useOrganizations` uses. `useHasAction` fails closed: it returns false while
 * loading, so an admin control stays hidden during the round-trip rather than
 * flashing then disappearing. Frontend gating is UX only; api-v2 is the
 * authority.
 */
import { useEffect, useRef, useState } from "react";
import { API_BASE_URL, isConfigured } from "../lib/config";
import { fetchUserPermissions, type UserPermissions } from "../lib/permissions";

export type PermissionsState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; permissions: UserPermissions };

export function useUserPermissions(
  orgId: string | null,
  token: string | null,
): PermissionsState {
  const [state, setState] = useState<PermissionsState>({ kind: "idle" });

  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!orgId || !isConfigured()) {
      setState({ kind: "idle" });
      return;
    }

    let cancelled = false;
    setState({ kind: "loading" });

    fetchUserPermissions(tokenRef.current, orgId, { apiBaseUrl: API_BASE_URL() })
      .then((permissions) => {
        if (!cancelled) setState({ kind: "loaded", permissions });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({ kind: "error", message: error instanceof Error ? error.message : String(error) });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [orgId]);

  return state;
}

export function hasAction(state: PermissionsState, action: string): boolean {
  return state.kind === "loaded" && state.permissions.actions.includes(action);
}
